import { useEffect, useMemo, useState } from 'react';
import { fetchPARBookingData, generateAvailableSlots, formatSlotTime, groupSlotsByDate } from '../lib/slots.js';

export default function SlotPicker({ value, onChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [openDate, setOpenDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPARBookingData()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('[slot-picker] failed to load slots', e);
          setError(e.message || 'Could not load available times.');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    if (!data?.link) return [];
    const slots = generateAvailableSlots(data.link, data.events || [], data.bookings || []);
    return groupSlotsByDate(slots);
  }, [data]);

  useEffect(() => {
    if (!openDate && grouped.length > 0) setOpenDate(grouped[0].date);
  }, [grouped, openDate]);

  if (loading) {
    return (
      <div className="slot-picker-shell" aria-busy="true">
        <div className="slot-picker-status">Loading available times…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="slot-picker-shell">
        <div className="slot-picker-error">{error}</div>
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <div className="slot-picker-shell">
        <div className="slot-picker-status">No times currently available. Write to hello@margin.consulting and I'll find you one.</div>
      </div>
    );
  }

  const selectedIso = typeof value === 'string' ? value : null;
  const selectedDate = selectedIso ? new Date(selectedIso) : null;

  return (
    <div className="slot-picker-shell">
      <div className="slot-picker-grid">
        <div className="slot-picker-dates" role="listbox" aria-label="Available dates">
          {grouped.map((g) => {
            const isOpen = g.date === openDate;
            const hasSelected = selectedDate && g.times.some((t) => t.toISOString() === selectedIso);
            return (
              <button
                key={g.date}
                type="button"
                role="option"
                aria-selected={hasSelected || isOpen}
                onClick={() => setOpenDate(g.date)}
                className={`slot-date-btn ${isOpen ? 'on' : ''} ${hasSelected ? 'has-selected' : ''}`}
              >
                <span className="slot-date-label">{g.date}</span>
                <span className="slot-date-count">{g.times.length} {g.times.length === 1 ? 'time' : 'times'}</span>
              </button>
            );
          })}
        </div>

        <div className="slot-picker-times">
          {(grouped.find((g) => g.date === openDate)?.times || []).map((t) => {
            const iso = t.toISOString();
            const isSelected = selectedIso === iso;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onChange(iso)}
                className={`slot-time-btn ${isSelected ? 'on' : ''}`}
                aria-pressed={isSelected}
              >
                {formatSlotTime(t)}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="slot-picker-confirm" role="status">
          <span className="slot-picker-confirm-label">Selected:</span>
          {' '}
          <span className="slot-picker-confirm-value">
            {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            {' at '}
            {formatSlotTime(selectedDate)}
          </span>
        </div>
      )}
    </div>
  );
}
